(function () {
  const config = window.freeGiftPromotionConfig;
  if (!config || !config.enabled) return;

  const giftVariantId = String(config.variantId || '');
  const promotionCode = config.promotionCode || 'silk_mask_88';
  const thresholdCents = Number(config.thresholdCents || 0);
  const cartTeaserMinimumCents = Number(config.cartTeaserMinimumCents || 0);
  const giftValueCents = Number(config.giftValueCents || 0);
  const expectedCurrency = String(config.currency || 'GBP').toUpperCase();
  const giftHandles = [config.giftProductHandle, config.retailGiftHandle].filter(Boolean);
  const propertyName = '_free_gift_promo';
  const mutationEventStart = 'snoooze:cart-mutation-start';
  const mutationEventEnd = 'snoooze:cart-mutation-end';
  const cartUpdatedEvent = 'snoooze:cart-updated';
  const declinedStorageKey = 'snoooze.free-gift-declined';
  const pendingRemovalStorageKey = 'snoooze.free-gift-removal-pending';
  const legacyChoiceStorageKey = 'snoooze.free-gift-offer-choice';
  const validResolutions = ['gift', 'discount'];

  let queue = Promise.resolve();
  let scheduledReconcile = null;
  let externalMutations = 0;
  let suppressObserversUntil = 0;
  let checkoutBypass = false;
  let checkoutFlowActive = false;
  let volatileDecline = { cartToken: '', declined: false };
  let lastCart = null;
  let manualGiftRemovalPending = false;

  function ajaxUrl(base) {
    return /\.js(?:\?|$)/.test(base) ? base : `${base}.js`;
  }

  async function requestJson(url, options) {
    const response = await fetch(url, options);
    let payload = null;

    try {
      payload = await response.json();
    } catch (error) {
      payload = null;
    }

    if (!response.ok || (payload && payload.status && payload.status >= 400)) {
      const message = payload && (payload.description || payload.message);
      throw new Error(message || `Cart request failed (${response.status})`);
    }

    return payload;
  }

  function jsonRequest(body) {
    return {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify(body)
    };
  }

  function getCart() {
    return requestJson(ajaxUrl(routes.cart_url), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    });
  }

  function isGiftVariant(item) {
    return String(item.variant_id) === giftVariantId;
  }

  function isExcludedItem(item) {
    const type = String(item.product_type || '').toLowerCase();
    return isGiftVariant(item) ||
      giftHandles.includes(item.handle) ||
      item.gift_card === true ||
      type === 'gift card' ||
      type === 'gift_card' ||
      type === 'ghost_product' ||
      type === 'giftbox_ghost_product';
  }

  function eligibleSubtotal(cart) {
    return cart.items.reduce((total, item) => {
      return isExcludedItem(item) ? total : total + Number(item.final_line_price || 0);
    }, 0);
  }

  function customerDiscountAmount(item) {
    return (item.discount_allocations || []).reduce((total, allocation) => {
      const application = allocation && (allocation.discount_application || allocation);
      const isCustomerCode = String(application && application.type || '').toLowerCase() === 'discount_code';
      return isCustomerCode ? total + Number(allocation.amount || 0) : total;
    }, 0);
  }

  function offerChoiceSubtotal(cart) {
    return cart.items.reduce((total, item) => {
      return isExcludedItem(item)
        ? total
        : total + Number(item.final_line_price || 0) + customerDiscountAmount(item);
    }, 0);
  }

  function isCurrencyEligible(cart) {
    return String(cart.currency || '').toUpperCase() === expectedCurrency;
  }

  function hasPromotionProperty(item) {
    return item.properties && item.properties[propertyName] === promotionCode;
  }

  function isGiftItem(item) {
    return isGiftVariant(item) && hasPromotionProperty(item);
  }

  function giftItems(cart) {
    return cart.items.filter(hasPromotionProperty);
  }

  function customerDiscountApplications(cart) {
    const applications = Array.isArray(cart.cart_level_discount_applications)
      ? [...cart.cart_level_discount_applications]
      : [];

    (cart.items || []).forEach((item) => {
      (item.discount_allocations || []).forEach((allocation) => {
        const application = allocation && (allocation.discount_application || allocation);
        if (application) applications.push(application);
      });
    });

    return applications.filter((application) => {
      return String(application && application.type || '').toLowerCase() === 'discount_code';
    });
  }

  function hasCustomerDiscountCode(cart) {
    const enteredCodes = Array.isArray(cart.discount_codes) ? cart.discount_codes : [];
    return enteredCodes.length > 0 || customerDiscountApplications(cart).length > 0;
  }

  function setCheckoutBusy(isBusy) {
    document.querySelectorAll('button[name="checkout"], input[name="checkout"]').forEach((button) => {
      if (isBusy) {
        if (button.dataset.freeGiftLock !== 'true') {
          button.dataset.freeGiftLock = 'true';
          button.dataset.freeGiftWasDisabled = button.disabled ? 'true' : 'false';
        }
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
      } else if (button.dataset.freeGiftLock === 'true') {
        if (button.dataset.freeGiftWasDisabled !== 'true') {
          button.disabled = false;
          button.removeAttribute('aria-disabled');
        }
        delete button.dataset.freeGiftLock;
        delete button.dataset.freeGiftWasDisabled;
      }
    });
  }

  function setPromotionState(state, message, eyebrow) {
    document.querySelectorAll('[data-free-gift-promotion]').forEach((element) => {
      element.dataset.state = state;
      element.classList.toggle('is-syncing', state === 'syncing');
      element.classList.toggle('has-error', state === 'error');

      const messageElement = element.querySelector('[data-free-gift-message]');
      if (messageElement && message) messageElement.textContent = message;

      const eyebrowElement = element.querySelector('[data-free-gift-eyebrow]');
      if (eyebrowElement && eyebrow) eyebrowElement.textContent = eyebrow;
    });
  }

  function cartToken(cart) {
    return String(cart && cart.token || '');
  }

  function giftDeclined(cart) {
    try {
      const value = JSON.parse(window.localStorage.getItem(declinedStorageKey));
      if (value && value.cartToken === cartToken(cart) && value.declined === true) return true;
    } catch (error) {
      // Fall through to the in-memory preference below.
    }

    return volatileDecline.cartToken === cartToken(cart) && volatileDecline.declined === true;
  }

  function storeGiftDeclined(cart, declined) {
    volatileDecline = {
      cartToken: cartToken(cart),
      declined: declined === true
    };

    try {
      if (declined !== true) {
        window.localStorage.removeItem(declinedStorageKey);
        return;
      }

      window.localStorage.setItem(declinedStorageKey, JSON.stringify({
        cartToken: cartToken(cart),
        declined: true
      }));
    } catch (error) {
      // Storage can be unavailable in privacy-restricted browsers. The current
      // reconciliation still completes using the in-memory preference.
    }
  }

  function clearGiftDeclined(cart) {
    if (volatileDecline.cartToken === cartToken(cart)) {
      volatileDecline = { cartToken: '', declined: false };
    }

    try {
      const value = JSON.parse(window.localStorage.getItem(declinedStorageKey));
      if (!value || value.cartToken === cartToken(cart)) {
        window.localStorage.removeItem(declinedStorageKey);
      }
    } catch (error) {
      try {
        window.localStorage.removeItem(declinedStorageKey);
      } catch (storageError) {
        // Nothing else to clear.
      }
    }
  }

  function consumePendingGiftRemoval(cart) {
    let pending = manualGiftRemovalPending;
    try {
      pending = pending || window.localStorage.getItem(pendingRemovalStorageKey) === 'true';
      window.localStorage.removeItem(pendingRemovalStorageKey);
    } catch (error) {
      // The in-memory flag still covers the current page.
    }
    manualGiftRemovalPending = false;
    if (pending) storeGiftDeclined(cart, true);
    return pending;
  }

  function clearLegacyChoice() {
    try {
      window.localStorage.removeItem(legacyChoiceStorageKey);
    } catch (error) {
      // The former preference is ignored even when storage cannot be changed.
    }
  }

  function renderResolution(contract) {
    document.querySelectorAll('.free-gift-promotion--cart').forEach((element) => {
      element.classList.toggle('is-unlocked', contract.qualifies);
      element.classList.toggle('has-conflict', contract.conflict);
      element.dataset.customerDiscountCode = contract.customerDiscountCodeApplied ? 'true' : 'false';
      element.dataset.giftDeclined = contract.declined ? 'true' : 'false';

      const conflict = element.querySelector('[data-free-gift-conflict]');
      if (conflict) conflict.hidden = !contract.conflict;

      const declined = element.querySelector('[data-free-gift-declined]');
      if (declined) declined.hidden = !contract.qualifies || !contract.declined;
    });
  }

  function focusResolution() {
    const resolution = document.querySelector('.free-gift-promotion--cart:not([hidden]) [data-free-gift-conflict]:not([hidden]) [data-free-gift-resolution]');
    if (resolution) resolution.focus({ preventScroll: false });
  }

  function setCartPromotionVisibility(subtotal) {
    document.querySelectorAll('.free-gift-promotion--cart').forEach((element) => {
      element.hidden = subtotal <= cartTeaserMinimumCents;
    });
  }

  function formatMoney(cents) {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: expectedCurrency,
      minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
      maximumFractionDigits: 2
    }).format(cents / 100);
  }

  function removeGiftLines(items) {
    if (!items.length) return Promise.resolve();

    const updates = {};
    items.forEach((item) => {
      updates[item.key] = 0;
    });
    return requestJson(ajaxUrl(routes.cart_update_url), jsonRequest({ updates }));
  }

  function normalizeGiftLines(items, canonicalItem) {
    const updates = {};
    items.forEach((item) => {
      updates[item.key] = item.key === canonicalItem.key ? 1 : 0;
    });

    const needsUpdate = items.some((item) => updates[item.key] !== item.quantity);
    if (!needsUpdate) return Promise.resolve();
    return requestJson(ajaxUrl(routes.cart_update_url), jsonRequest({ updates }));
  }

  function addGift() {
    return requestJson(ajaxUrl(routes.cart_add_url), jsonRequest({
      items: [{
        id: giftVariantId,
        quantity: 1,
        properties: { [propertyName]: promotionCode }
      }]
    }));
  }

  function clearCustomerDiscountCodes() {
    return requestJson(ajaxUrl(routes.cart_update_url), jsonRequest({ discount: '' }));
  }

  function sectionInnerHtml(html, selector) {
    if (!html) return null;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const selected = parsed.querySelector(selector);
    return selected ? selected.innerHTML : null;
  }

  async function refreshCartSections(cart) {
    // Shopify can briefly return stale cart-footer totals while automatic
    // discounts are recalculated after a gift line is added or removed. On the
    // full cart page, reload once after the mutation so line items, PACK and
    // subtotal are rendered from the same server-side cart snapshot. The next
    // reconcile is stable and does not reload again.
    if (document.querySelector('#MainContent #main-cart-items')) {
      setCheckoutBusy(true);
      window.location.reload();
      return;
    }

    const sectionNames = ['mini-cart', 'cart-icon-bubble', 'main-cart-items', 'main-cart-footer'];
    const separator = routes.cart_url.includes('?') ? '&' : '?';
    const url = `${routes.cart_url}${separator}sections=${sectionNames.join(',')}`;
    const sections = await requestJson(url, { credentials: 'same-origin' });

    suppressObserversUntil = Date.now() + 600;

    const miniCart = document.getElementById('mini-cart');
    if (miniCart && sections['mini-cart']) {
      const replacement = sectionInnerHtml(sections['mini-cart'], '.shopify-section');
      if (replacement !== null) miniCart.innerHTML = replacement;
    }

    const cartIcon = document.getElementById('cart-icon-bubble');
    if (cartIcon && sections['cart-icon-bubble']) {
      const replacement = sectionInnerHtml(sections['cart-icon-bubble'], '.shopify-section');
      if (replacement !== null) cartIcon.innerHTML = replacement;
    }

    const mainCartItems = document.querySelector('#MainContent #main-cart-items');
    if (mainCartItems && sections['main-cart-items']) {
      const replacement = sectionInnerHtml(sections['main-cart-items'], '.js-contents');
      const contents = mainCartItems.querySelector('.js-contents');
      if (contents && replacement !== null) contents.innerHTML = replacement;
    }

    const mainCartFooter = document.getElementById('main-cart-footer');
    if (mainCartFooter && sections['main-cart-footer']) {
      const replacement = sectionInnerHtml(sections['main-cart-footer'], '.js-contents');
      const contents = mainCartFooter.querySelector('.js-contents');
      if (contents && replacement !== null) contents.innerHTML = replacement;
    }

    const isEmpty = Number(cart && cart.item_count) === 0;
    const cartItems = document.querySelector('cart-items');
    if (cartItems) cartItems.classList.toggle('is-empty', isEmpty);
    if (mainCartFooter) mainCartFooter.classList.toggle('is-empty', isEmpty);

    setCheckoutBusy(true);
  }

  function assertCartContract(cart, declined) {
    const gifts = giftItems(cart);
    const subtotal = offerChoiceSubtotal(cart);
    const postDiscountSubtotal = eligibleSubtotal(cart);
    const qualifies = isCurrencyEligible(cart) && subtotal >= thresholdCents;
    const customerDiscountCodeApplied = hasCustomerDiscountCode(cart);
    const conflict = qualifies && customerDiscountCodeApplied && gifts.length > 0;

    if (!qualifies && gifts.length) {
      throw new Error('Free gift remained in an ineligible cart.');
    }

    if (qualifies && declined && gifts.length) {
      throw new Error('Free gift returned after the shopper removed it.');
    }

    if (qualifies && !declined) {
      if (gifts.length !== 1 || gifts[0].quantity !== 1 || !isGiftItem(gifts[0])) {
        throw new Error('Free gift quantity or promotion marker is invalid.');
      }
      if (Number(gifts[0].original_line_price) !== giftValueCents) {
        throw new Error('The retail mask base price is invalid.');
      }
      if (Number(gifts[0].final_line_price) !== 0 && !conflict) {
        throw new Error('The inactive or misconfigured gift discount did not make the mask free.');
      }
    }

    return {
      qualifies,
      declined: qualifies && declined,
      gifts,
      subtotal,
      postDiscountSubtotal,
      customerDiscountCodeApplied,
      conflict
    };
  }

  async function runReconcile(options) {
    const shouldRefresh = !options || options.refresh !== false;
    const preferredResolution = options && options.preferredResolution;
    let changed = false;

    setCheckoutBusy(true);
    setPromotionState('syncing', 'Checking your free gift…');

    try {
      clearLegacyChoice();
      let cart = await getCart();
      lastCart = cart;
      consumePendingGiftRemoval(cart);
      let gifts = giftItems(cart);
      let qualifies = isCurrencyEligible(cart) && offerChoiceSubtotal(cart) >= thresholdCents;

      if (qualifies && preferredResolution === 'gift') {
        storeGiftDeclined(cart, false);
        if (hasCustomerDiscountCode(cart)) {
          // This control is shown only after a real conflict and explicitly
          // tells the shopper that keeping the gift removes the code.
          await clearCustomerDiscountCodes();
          changed = true;
          cart = await getCart();
          lastCart = cart;
          gifts = giftItems(cart);
          qualifies = isCurrencyEligible(cart) && offerChoiceSubtotal(cart) >= thresholdCents;
          if (hasCustomerDiscountCode(cart)) {
            throw new Error('The applied customer discount could not be removed.');
          }
        }
      } else if (qualifies && preferredResolution === 'discount') {
        storeGiftDeclined(cart, true);
      }

      let declined = qualifies && giftDeclined(cart);

      if (!qualifies) {
        clearGiftDeclined(cart);
        declined = false;
        if (gifts.length) {
          await removeGiftLines(gifts);
          changed = true;
        }
      } else if (declined) {
        if (gifts.length) {
          await removeGiftLines(gifts);
          changed = true;
        }
      } else {
        const canonicalGift = gifts.find(isGiftItem);
        if (!canonicalGift) {
          if (gifts.length) await removeGiftLines(gifts);
          await addGift();
          changed = true;
        } else if (gifts.length !== 1 || canonicalGift.quantity !== 1) {
          await normalizeGiftLines(gifts, canonicalGift);
          changed = true;
        }
      }

      if (changed) {
        cart = await getCart();
        lastCart = cart;
      }

      // Converge once after Shopify recalculates discounts. A customer code is
      // allowed to coexist temporarily with the gift only as an unresolved
      // conflict; checkout is blocked until the shopper keeps one offer.
      qualifies = isCurrencyEligible(cart) && offerChoiceSubtotal(cart) >= thresholdCents;
      declined = qualifies && giftDeclined(cart);
      gifts = giftItems(cart);

      if (!qualifies) {
        clearGiftDeclined(cart);
        declined = false;
        if (gifts.length) {
          await removeGiftLines(gifts);
          changed = true;
          cart = await getCart();
          lastCart = cart;
        }
      }

      const contract = assertCartContract(cart, declined);

      if (changed && shouldRefresh) await refreshCartSections(cart);

      setCartPromotionVisibility(contract.subtotal);
      renderResolution(contract);

      if (contract.conflict) {
        setPromotionState('conflict', 'Remove the free mask to use your discount, or remove the discount to keep the mask.', 'Please choose one');
      } else if (contract.qualifies && contract.declined) {
        const message = contract.customerDiscountCodeApplied
          ? 'Free mask removed. Your discount is kept.'
          : 'Free mask removed from this cart.';
        setPromotionState('discount', message, 'Free gift removed');
      } else if (contract.qualifies) {
        setPromotionState('added', `${formatMoney(giftValueCents)} silk mask added automatically at £0.`, 'Free gift added');
      } else {
        const remaining = Math.max(0, thresholdCents - contract.subtotal);
        setPromotionState('locked', `Add ${formatMoney(remaining)} more.`, `Free silk mask at ${formatMoney(thresholdCents)}`);
      }

      document.dispatchEvent(new CustomEvent('snoooze:free-gift-reconciled', {
        detail: { changed, ...contract }
      }));

      return { cart, changed, ...contract };
    } catch (error) {
      console.error('[Free gift promotion]', error);
      setPromotionState('error', 'We could not update your free gift. Please retry or contact us.');
      throw error;
    } finally {
      setCheckoutBusy(false);
    }
  }

  function reconcile(options) {
    queue = queue.catch(() => undefined).then(() => runReconcile(options));
    return queue;
  }

  function scheduleReconcile() {
    if (Date.now() < suppressObserversUntil) return;
    window.clearTimeout(scheduledReconcile);
    scheduledReconcile = window.setTimeout(() => {
      reconcile().catch(() => undefined);
    }, 120);
  }

  function waitForCartIdle(timeoutMs) {
    const startedAt = Date.now();
    return new Promise((resolve, reject) => {
      const check = () => {
        if (externalMutations === 0) {
          resolve();
        } else if (Date.now() - startedAt >= timeoutMs) {
          reject(new Error('Cart update did not finish before checkout.'));
        } else {
          window.setTimeout(check, 50);
        }
      };
      check();
    });
  }

  document.addEventListener(mutationEventStart, () => {
    externalMutations += 1;
    setCheckoutBusy(true);
  });

  document.addEventListener(mutationEventEnd, () => {
    externalMutations = Math.max(0, externalMutations - 1);
    if (externalMutations === 0) scheduleReconcile();
  });

  document.addEventListener(cartUpdatedEvent, scheduleReconcile);

  window.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;

    const giftRemove = event.target.closest('[data-free-gift-remove]');
    if (giftRemove) {
      manualGiftRemovalPending = true;
      if (lastCart) storeGiftDeclined(lastCart, true);
      try {
        window.localStorage.setItem(pendingRemovalStorageKey, 'true');
      } catch (error) {
        // The in-memory marker still prevents the automatic gift from looping
        // back into the cart during this page view.
      }
      return;
    }

    const target = event.target.closest('[data-free-gift-resolution]');
    if (!target) return;

    event.preventDefault();
    const preferredResolution = target.dataset.freeGiftResolution;
    if (!validResolutions.includes(preferredResolution)) return;

    reconcile({ preferredResolution }).catch(() => undefined);
  }, true);

  async function handleCheckout(event) {
    if (checkoutBypass) return;

    if (checkoutFlowActive) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
    checkoutFlowActive = true;
    setCheckoutBusy(true);

    try {
      await waitForCartIdle(8000);
      const result = await reconcile({ refresh: false });
      if (result.conflict) {
        setPromotionState('conflict', 'Remove the free mask to use your discount, or remove the discount to keep the mask.', 'Please choose one');
        renderResolution(result);
        focusResolution();
        return;
      }

      checkoutBypass = true;
      window.location.assign('/checkout');
    } catch (error) {
      checkoutBypass = false;
    } finally {
      checkoutFlowActive = false;
      if (!checkoutBypass) setCheckoutBusy(false);
    }
  }

  // Capture on window before cart-app click listeners. This keeps the offer
  // contract authoritative even when an upsell app prevents form submission.
  window.addEventListener('click', (event) => {
    const checkout = event.target instanceof Element
      ? event.target.closest('[name="checkout"], [data-free-gift-checkout]')
      : null;
    if (!checkout) return;
    handleCheckout(event);
  }, true);

  window.addEventListener('submit', (event) => {
    const submitter = event.submitter || document.activeElement;
    if (!submitter || submitter.name !== 'checkout') return;
    handleCheckout(event);
  }, true);

  window.freeGiftPromotion = {
    config,
    getCart,
    eligibleSubtotal,
    offerChoiceSubtotal,
    hasCustomerDiscountCode,
    assertCartContract,
    reconcile,
    giftDeclined
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      reconcile().catch(() => undefined);
    }, { once: true });
  } else {
    reconcile().catch(() => undefined);
  }
})();
